import React, { useState } from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { sanitizeToken } from "../../utils/adNameBuilder";

// Stored "<base>x<v1>x<v2>" -> { base, variations }; unknown bases keep their first segment.
export function splitTwoLevelToken(token, bases) {
  const t = String(token || "");
  if (!t) return { base: "", variations: [] };
  const known = bases.find((o) => t === o.value || t.startsWith(`${o.value}x`));
  const base = known ? known.value : t.split("x")[0];
  const rest = t.slice(base.length).replace(/^x/, "");
  return { base, variations: rest ? rest.split("x").filter(Boolean) : [] };
}

export const joinTwoLevelToken = (base, variations) =>
  base ? [base, ...new Set(variations.map(sanitizeToken).filter(Boolean))].join("x") : "";

// Grid cell: pick a base, tick its variations, or add a custom one; the cell shows the stored token.
export default function TwoLevelCell({ value, bases = [], variationsFor, onChange, placeholder = "—" }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const { base, variations } = splitTwoLevelToken(value, bases);
  const options = variationsFor(base);
  const presets = new Set(options.map((o) => o.value));
  const customs = variations.filter((v) => !presets.has(v));

  const setBase = (b) => onChange(joinTwoLevelToken(b, []));
  const toggle = (v) => onChange(joinTwoLevelToken(base, variations.includes(v) ? variations.filter((x) => x !== v) : [...variations, v]));
  const addCustom = () => {
    if (!sanitizeToken(custom)) return;
    onChange(joinTwoLevelToken(base, [...variations, custom]));
    setCustom("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full min-w-[190px] items-center justify-between gap-2 bg-transparent py-1.5 px-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
        >
          <span className={`truncate font-mono text-[13px] ${value ? "" : "font-sans text-muted-foreground"}`}>{value || placeholder}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="max-h-44 overflow-y-auto p-1">
          <p className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Base</p>
          {bases.map((o) => (
            <button key={o.value} type="button" onClick={() => setBase(o.value)} className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent">
              <span>{o.label}</span>
              {o.value === base && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
        {base && (
          <div className="border-t border-border p-1">
            <p className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Variations</p>
            <div className="max-h-40 overflow-y-auto">
              {options.map((o) => (
                <button key={o.value} type="button" onClick={() => toggle(o.value)} className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent">
                  <span>{o.label}</span>
                  {variations.includes(o.value) && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
              {customs.map((v) => (
                <button key={v} type="button" onClick={() => toggle(v)} title="Click to remove" className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 font-mono text-sm hover:bg-accent">
                  <span>{v}</span>
                  <Check className="h-4 w-4 text-primary" />
                </button>
              ))}
            </div>
            <div className="mt-1 flex items-center gap-1 border-t border-border px-1 pt-1.5">
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                placeholder="New variation"
                className="h-8 min-w-0 flex-1 rounded-sm border border-input px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <button type="button" onClick={addCustom} disabled={!sanitizeToken(custom)} className="flex h-8 w-8 items-center justify-center rounded-sm text-primary hover:bg-accent disabled:opacity-40">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
